import type { PropsWithChildren } from 'react';
import { createContext, useMemo } from 'react';
import { getColorWithAlpha, useContextOrThrow } from '@elfsight/lib-utils';
import type { CheckoutColors } from '../../../common';

export interface CheckoutColorsContextValue extends CheckoutColors {
  // Derived colors
  headingColor: string;
  captionColor: string;
  submitButtonColor: string;

  // Semitransparent colors (frequently used)
  textColor70A: string;
  textColor50A: string;
  textColor20A: string;

  // Field colors
  fieldLabelColor: string;
  fieldTextColor: string;
  fieldHintColor: string;
  fieldBorderColor: string;
  fieldActiveColor: string;
  fieldInsideLabelColor: string;
  fieldFillColor: string;
}

const CheckoutColorsContext = createContext<
  CheckoutColorsContextValue | undefined
>(undefined);

export function CheckoutColorsProvider({
  textColor,
  accentColor,
  errorColor,
  successColor,
  backgroundColor,
  children
}: PropsWithChildren<CheckoutColors>) {
  const colors = useMemo((): CheckoutColorsContextValue => {
    const textColor70A = getColorWithAlpha(textColor, 0.7);
    const textColor50A = getColorWithAlpha(textColor, 0.5);
    const textColor20A = getColorWithAlpha(textColor, 0.2);

    return {
      // Base colors
      textColor,
      accentColor,
      errorColor,
      successColor,
      backgroundColor,

      // Semitransparent colors (frequently used)
      textColor70A,
      textColor50A,
      textColor20A,

      // Derived colors
      headingColor: textColor,
      captionColor: textColor50A,
      submitButtonColor: accentColor,

      // Field colors
      fieldLabelColor: textColor,
      fieldTextColor: textColor,
      fieldHintColor: textColor50A,
      fieldBorderColor: textColor20A,
      fieldActiveColor: accentColor,
      fieldInsideLabelColor: textColor50A,
      fieldFillColor: 'transparent'
    };
  }, [textColor, accentColor, errorColor, successColor, backgroundColor]);

  return (
    <CheckoutColorsContext.Provider value={colors}>
      {children}
    </CheckoutColorsContext.Provider>
  );
}

export function useCheckoutColors(): CheckoutColorsContextValue {
  return useContextOrThrow(CheckoutColorsContext, 'CheckoutColors');
}
